



































































































































































































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import CommonVue from '../models/commonVue';
import { BusEvent } from '../models/bus';
import { MediaFileField } from '../models/fields';

import Exercise from '../api/exercise';
import LockManager from '../models/lock';
import Media from '../api/media';

import FieldAudio from '../components/FieldAudio.vue';
import FieldImage from '../components/FieldImage.vue';
import FieldTranscriptArpabet from '../components/FieldTranscriptArpabet.vue';
import FieldSentenceLinks from '../components/FieldSentenceLinks.vue';
import FieldInternational from '../components/FieldInternational.vue';
import FieldVideos from '../components/FieldVideos.vue';
import FieldPhonemes from '../components/FieldPhonemes.vue';
import FieldHints from '../components/FieldHints.vue';
import EditIcon from '../components/icons/EditIcon.vue';
import FieldVideosI18n from '../components/FieldVideosI18n.vue';
import DropDownText from '@/components/DropDownText.vue';
import { ExerciseGameSubType, ExerciseScoringMode } from '@/api/common';
import FieldReferenceAlignments from '@/components/FieldReferenceAlignments.vue';
import FieldExtraInfo from '@/components/FieldExtraInfo.vue';
import CopyExercise from '@/components/CopyExercise.vue';
import { isEmpty, isNil } from 'lodash';
import Module from '@/api/module';
import { checkContent } from '@/api/content-check';

@Component({
  components: {
    'field-extra-info': FieldExtraInfo,
    'field-reference-alignments': FieldReferenceAlignments,
    'field-audio': FieldAudio,
    'field-image': FieldImage,
    'field-transcript-arpabet': FieldTranscriptArpabet,
    'field-sentence-links': FieldSentenceLinks,
    'field-international': FieldInternational,
    'field-videos': FieldVideos,
    'field-phonemes': FieldPhonemes,
    'field-hints': FieldHints,
    'edit-icon': EditIcon,
    'field-videos-i18n': FieldVideosI18n,
    'drop-down-text': DropDownText,
    'copy-exercise': CopyExercise
  }
})
export default class EditExercisePronunciation extends CommonVue {
  @Prop({ type: Number }) public moduleId!: number;
  @Prop({ type: Number }) public lessonId!: number;
  @Prop({ type: Exercise }) public exercise!: Exercise;
  @Prop({ type: String, default: Module.SLUG }) public parent!: string;

  public gameSubTypeOptions = ExerciseGameSubType.OPTIONS;
  public gameSubTypePresentation = ExerciseGameSubType.PRESENTATION;
  public scoringModeOptions = ExerciseScoringMode.OPTIONS;
  public scoringModePresentation = ExerciseScoringMode.PRESENTATION;
  public isCheckAudio: boolean = undefined;

  public async beforeMount() {
    await this.load();
  }

  @Watch('exercise')
  public async load() {
    this.busy = true;
    this.error = false;

    if (this.exercise && this.exercise.id) {
      this.lockManager = new LockManager(this.EXERCISE_SLUG, this.exercise.id);
      await this.lockManager.checkStatus();
      if (this.lockManager.isBusy) {
        this.$bus.$emit(
          BusEvent.ERROR,
          'Someone else is making changes.',
          new Error(
            'Click refresh to check if they have finished their changes'
          )
        );
      }
    } else {
      this.lockManager = null;
    }

    this.busy = false;
  }

  // enter edit mode if it can get the lock
  @Watch('exercise', { deep: true })
  public async enterEditMode() {
    await this.tryToLock();
  }

  public async deleteClicked(): Promise<void> {
    if (!this.exercise || !this.exercise.id) {
      return;
    }

    this.busy = true;
    this.exercise.isDirty = false;

    try {
      if (!this.lockManager) {
        this.lockManager = new LockManager(
          this.EXERCISE_SLUG,
          this.exercise.id
        );
      }

      await this.tryToLock();

      await this.exercise.delete();

      await this.lockManager.releaseLock();

      this.busy = false;
      this.$router.push(
        `/${this.parent}/${this.moduleId}/${this.LESSON_SLUG}/${this.lessonId}/${this.EXERCISE_SLUG}`
      );
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not delete exercise', e);
    }
  }

  public async saveClicked(): Promise<void> {
    if (!this.exercise) {
      return;
    }

    this.busy = true;
    this.exercise.isDirty = false;

    try {
      this.checkMandatoryFields();

      await this.saveExercise();
      this.busy = false;

      const isContentOkay = await this.performContentCheck();
      if (!isContentOkay) return;

      this.$router.push(
        `/${this.parent}/${this.moduleId}/${this.LESSON_SLUG}/${this.lessonId}/${this.EXERCISE_SLUG}`
      );
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not save exercise', e);
    }
  }

  public checkMandatoryFields() {
    // Check if speaking sentence was chosen
    const words = this.exercise.speaking_content.transcript_arpabet;
    if (words === undefined || words.length < 1) {
      throw Error('Please select at least one word for your sentence.');
    }

    // Check if speaking audio was chosen
    if (!(this.$refs.speaking_audio as MediaFileField).hasMedia()) {
      throw Error('Please select an audio file.');
    }
  }

  public async performContentCheck() {
    const data = await Exercise.getJson(this.exercise.id);
    const result = await checkContent(data);

    if (isEmpty(result.result)) {
      return true;
    }

    this.$bus.$emit(BusEvent.ERROR, 'Could not save exercise', {
      messageItems: result.result.map((item) => ({
        message: item.message,
        key: item.word || item.sentence
      }))
    });

    return false;
  }

  public async checkAudio(): Promise<void> {
    this.busy = true;
    this.exercise.isDirty = false;

    try {
      const sentence = this.exercise.speaking_content.sentence;
      const audio = this.exercise.speaking_content.audio_path;
      if (
        (sentence === undefined || isNil(sentence)) &&
        (audio === undefined || isNil(audio))
      ) {
        throw Error(
          'Elsa sentence/audio are missing. You must input sentence/audio file for ELSA'
        );
      } else {
        const result = await this.exercise.checkAudioSpeaking();
        if (result && result.stream_score_type === 'correct') {
          if (!isNil(this.exercise.speaking_content.transcription)) {
            this.exercise.speaking_content.transcription = result.sentence_ipa
              ? result.sentence_ipa
              : '';
          }
          this.isCheckAudio = true;
          setTimeout(() => {
            this.isCheckAudio = undefined;
          }, 1000);
        } else {
          this.isCheckAudio = false;
          setTimeout(() => (this.isCheckAudio = undefined), 1000);
        }
      }
      this.busy = false;
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not check audio', e);
    }
  }

  public async saveExercise() {
    let lessonLockManager: LockManager;

    if (!this.exercise.id) {
      lessonLockManager = new LockManager(this.LESSON_SLUG, this.lessonId);
      await lessonLockManager.tryAcquireLock();

      // Upload exercise
      await this.exercise.save();

      // call it again to save media files of content
      await this.saveExercise();
    } else {
      if (!this.lockManager && this.exercise && this.exercise.id) {
        this.lockManager = new LockManager(
          this.EXERCISE_SLUG,
          this.exercise.id
        );
      }
      await this.tryToLock();

      if (!this.exercise.speaking_content.id) {
        this.exercise.speaking_content.parent_exercise = this.exercise.id;
        await this.exercise.speaking_content.save();
      }
      await this.saveMediaFiles();
      await FieldHints.saveHints(
        this.exercise.speaking_content.global_hints_objects,
        this.exercise.speaking_content.id,
        'exercisespeakingcontent'
      );
      // Upload items with media ids
      // await this.exercise.updateReferenceScore();
      await this.exercise.speaking_content.save();
      await this.exercise.save();
    }

    if (this.lockManager && !this.lockManager.isAvailable) {
      await this.lockManager.releaseLock();
    }

    if (lessonLockManager) {
      await lessonLockManager.releaseLock();
    }
  }

  public async saveMediaFiles(): Promise<void> {
    if (!this.exercise || !this.exercise.id) {
      throw Error('Exercise has not been created yet.');
    }

    this.imagesToDelete.forEach((id) => Media.deleteImage(id));
    this.imagesToDelete = [];

    this.audioToDelete.forEach((id) => Media.deleteAudio(id));
    this.audioToDelete = [];

    const speaking_audio = await this.saveMediaFile(
      this.$refs.speaking_audio,
      'audio_path'
    );
    if (speaking_audio !== null) {
      this.exercise.speaking_content.audio_path = speaking_audio;
    }
    const speaking_audio_alt = await this.saveMediaFile(
      this.$refs.speaking_audio_alt,
      'audio_alt_path'
    );
    if (speaking_audio_alt !== null) {
      this.exercise.speaking_content.audio_alt_path = speaking_audio_alt;
    }
    const speaking_image = await this.saveMediaFile(
      this.$refs.speaking_image,
      'image_path'
    );
    if (speaking_image !== null) {
      this.exercise.speaking_content.image_path = speaking_image;
    }
  }

  public async saveMediaFile(
    field: any,
    fieldName: string
  ): Promise<number | null | undefined> {
    if (!this.exercise || !this.exercise.id) {
      return null;
    }
    const mediaFileField = (field as any) as MediaFileField;

    if (mediaFileField.hasNewMedia()) {
      return await mediaFileField.upload(
        this.EXERCISE_SLUG,
        this.exercise.id,
        fieldName
      );
    } else if (!mediaFileField.hasMedia()) {
      return undefined;
    } else {
      return null;
    }
  }

  public async jsonView(): Promise<void> {
    this.$router.push(`/json/${this.exercise.id}`);
  }

  public async getReferenceScore(): Promise<void> {
    try {
      this.checkMandatoryFields();
      await this.exercise.updateReferenceScore();
    } catch (e) {
      this.busy = false;
      this.$bus.$emit(BusEvent.ERROR, 'Could not update score exercise', e);
    }
  }
}
